import type { IPageInfo } from '~/modules/page-analytic/types';
import type { IPathAnalytic } from '../types';

import { findCacheKeyByRequestId } from '~/modules/apps/websocket';
import { EAnalyticMode } from '~/modules/analytics/types';
import { deepCopy } from '~/utils';
import { create } from 'zustand';

export interface IPathAnalyticCacheValue {
  requestId?: string;
  data?: IPathAnalytic[];
}
export type IPathAnalyticDataCache = Record<string, IPathAnalyticCacheValue>;

export interface IPathAnalyticStoreState {
  cacheData: IPathAnalyticDataCache;
  pagesInfo: IPageInfo;
  progress: number;
  isShowBannerProgress: boolean;
  dataKeyErrors: any[];
  analyticMode: EAnalyticMode;
  firstLevelLocationPath?: string;
  setProgress: (progress: number) => void;
  upsertCacheData: (key: string, value: IPathAnalyticCacheValue) => void;
  getCacheData: () => IPathAnalyticDataCache;
  findCacheKeyByRequestId: (requestId: string) => string | undefined;
  setIsShowBannerProgress: (isShowBannerProgress: boolean) => void;
  setDataKeyErrors: (value: any[]) => void;
  checkAndDeleteDataKeyErrors: (value: any[]) => void;
  setAnalyticMode: (analyticMode: EAnalyticMode) => void;
  upsertPagesInfo: (value: IPageInfo) => void;
  setFirstLevelLocationPath: (firstLevelLocationPath?: string) => void;
}
export const usePathAnalysisStore = create<IPathAnalyticStoreState>()((set, get) => ({
  cacheData: {},
  pagesInfo: {},
  progress: 0,
  isShowBannerProgress: true,
  dataKeyErrors: [],
  analyticMode: EAnalyticMode.ALL_SESSION,
  firstLevelLocationPath: undefined,
  upsertCacheData: (key, value) => {
    const currentCampaigns = deepCopy(get().cacheData);
    const updatedCampaigns = {
      ...currentCampaigns,
      [key]: {
        requestId: currentCampaigns[key]?.requestId ?? '',
        ...value,
      },
    };

    set({ cacheData: updatedCampaigns });
  },
  getCacheData: () => get().cacheData,
  findCacheKeyByRequestId: (requestId) => findCacheKeyByRequestId(get().cacheData, requestId),
  setProgress: (progress) => set({ progress }),
  setIsShowBannerProgress: (isShowBannerProgress) => set({ isShowBannerProgress }),
  setDataKeyErrors: (value: any[]) => {
    const dataKeyErrors = deepCopy(get().dataKeyErrors);
    const isExisting =
      dataKeyErrors.findIndex((item) => JSON.stringify(item) === JSON.stringify(value)) > -1;
    !isExisting && dataKeyErrors.push(value);

    set({ dataKeyErrors });
  },
  checkAndDeleteDataKeyErrors: (value: any[]) => {
    const dataKeyErrors = deepCopy(get().dataKeyErrors);
    const isExisting =
      dataKeyErrors.findIndex((item) => JSON.stringify(item) === JSON.stringify(value)) > -1;

    isExisting && dataKeyErrors.splice(dataKeyErrors.indexOf(value), 1);

    set({ dataKeyErrors });
  },
  upsertPagesInfo: (newPageInfo) => {
    set({ pagesInfo: { ...get().pagesInfo, ...newPageInfo } });
  },
  setAnalyticMode: (analyticMode) => set({ analyticMode }),
  setFirstLevelLocationPath: (firstLevelLocationPath) => set({ firstLevelLocationPath }),
}));
